import { useState } from 'react';
import { authClient } from '../api/client.js';
import { useDictionary } from '../i18n/context.js';

// ---------------------------------------------------------------------------
// The application's own front door (structure spec 5.3, rewritten by this
// task -- Cloudflare Access used to own this). `App.tsx` renders this route
// for any request with no live session, carrying the path the operator was
// actually headed to in `?redirect=`.
//
// This component never navigates on success -- it only calls
// `authClient.signIn.email` and lets the error surface (or not). The actual
// "go where the operator was headed" behavior lives entirely in `App.tsx`:
// `authClient`'s session store is shared and reactive, so the moment sign-in
// resolves, `App.tsx`'s own `useSession()` re-renders into the authenticated
// route tree, which reads the same `?redirect=` still sitting in the URL
// (see `sanitizeRedirectTarget`, exported from there) and redirects from
// there. Doing it in one place, reactively, avoids a race a `navigate()`
// call here would have against that same re-render.
//
// The failure message is identical whether the address has no account or
// the password is wrong -- `authClient.signIn.email` surfaces both as the
// same `error` field (apps/worker/test/auth.test.ts's "does not reveal
// whether an address has an account" proves the Worker already answers both
// with the same status and body), and this component never branches on the
// error's contents, only on whether one exists. Do not add a second message
// for "no such account" -- that would reopen the enumeration hole the Worker
// side closed.
// ---------------------------------------------------------------------------

/**
 * Only a same-app relative path is ever a valid redirect target -- `raw`
 * comes straight from a URL query parameter, so it is attacker-controlled
 * (a crafted link to `/sign-in?redirect=https://evil.example`). Rejecting
 * anything that isn't a single leading `/` also blocks the
 * protocol-relative form (`//evil.example`), which a browser resolves as a
 * different origin despite starting with a slash.
 *
 * Exported for `App.tsx`, the only place this is actually called from --
 * see that file's own comment for why.
 */
export const sanitizeRedirectTarget = (raw: string | null): string => {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return '/home';
  return raw;
};

export const SignInRoute = (): React.JSX.Element => {
  const dict = useDictionary();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    setError(null);
    setBusy(true);
    try {
      const { error: signInError } = await authClient.signIn.email({ email, password });
      if (signInError) {
        setError(dict.ui.auth.failure);
        setBusy(false);
      }
      // On success, deliberately do nothing further here -- see the header
      // comment. `busy` is left `true` until `App.tsx`'s reactive gate swaps
      // this screen out, so the form stays disabled rather than briefly
      // re-enabling between a successful sign-in and that swap.
    } catch {
      setError(dict.ui.auth.failure);
      setBusy(false);
    }
  };

  const canSubmit = !busy && email.trim() !== '' && password !== '';

  return (
    <div className="stack" style={{ maxWidth: 420, margin: 'var(--sp-8) auto', width: '100%' }}>
      <form
        className="panel"
        onSubmit={(e) => { e.preventDefault(); if (canSubmit) void submit(); }}
      >
        <div className="panel__head"><h1 className="t-section">{dict.ui.auth.title}</h1></div>
        <div className="panel__body stack">
          {error && (
            <div className="alert alert--danger">
              <div className="alert__body">{error}</div>
            </div>
          )}

          <div className="field">
            <label className="field__label" htmlFor="sign-in-email">{dict.ui.auth.emailLabel}</label>
            <input
              id="sign-in-email"
              className="input"
              type="email"
              autoComplete="username"
              value={email}
              disabled={busy}
              onChange={(e) => { setEmail(e.target.value); setError(null); }}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="sign-in-password">{dict.ui.auth.passwordLabel}</label>
            <input
              id="sign-in-password"
              className="input"
              type="password"
              autoComplete="current-password"
              value={password}
              disabled={busy}
              onChange={(e) => { setPassword(e.target.value); setError(null); }}
            />
          </div>

          <div className="row" style={{ gap: 'var(--sp-3)' }}>
            <button type="submit" className="btn btn--primary" disabled={!canSubmit}>
              {busy ? dict.ui.auth.submitting : dict.ui.auth.submit}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};
